// supabaseClient.js
import { createClient } from '@supabase/supabase-js';
import axios from 'axios';
import { REST_API_END_POINT } from '../constants/DefaultValues';

// Set in frontend/.env. Publishable key only (never the secret key) — it is bundled into the browser
// and has no storage rights; uploads and downloads go through the backend attachment proxy.
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://iesnxcfhhtlsfqnfpwkt.supabase.co';
const supabaseKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY || 'missing-supabase-publishable-key';

const ATTACHMENTS_BUCKET = 'signwise-attachments';
const PUBLIC_MARKER = `/storage/v1/object/public/${ATTACHMENTS_BUCKET}/`;
const AUTH_MARKER = `/storage/v1/object/authenticated/${ATTACHMENTS_BUCKET}/`;
const PROXY_MARKER = '/attachments/file?path=';

export const supabase = createClient(supabaseUrl, supabaseKey);

function ensureHttpsProxyUrl(url) {
  if (
    typeof window !== 'undefined' &&
    window.location?.protocol === 'https:' &&
    typeof url === 'string' &&
    url.startsWith('http://')
  ) {
    return `https://${url.slice('http://'.length)}`;
  }
  return url;
}

function buildProxyAttachmentUrl(path) {
  return ensureHttpsProxyUrl(`${REST_API_END_POINT}attachments/file?path=${encodeURIComponent(path)}`);
}

/** Keep upload key + returned URL in sync — Supabase rejects many special chars in object names. */
function sanitizeAttachmentStoragePath(path) {
  if (typeof path !== 'string' || !path) return path;
  const parts = path.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (!parts.length) return path;
  const last = parts.length - 1;
  const cleaned = parts[last].replace(/\s+/g, '_').replace(/[^a-zA-Z0-9.\-_]/g, '');
  parts[last] = cleaned || 'file';
  return parts.join('/');
}

// supabase.storage is a getter (new client each access) — patch the prototype once.
const storageFileApiProto = Object.getPrototypeOf(supabase.storage.from(ATTACHMENTS_BUCKET));
const originalGetPublicUrl = storageFileApiProto.getPublicUrl;
const originalUpload = storageFileApiProto.upload;

// The bucket is private and the browser key has no storage rights: files are sent to the
// backend, which uploads them with the server key.
storageFileApiProto.upload = async function patchedUpload(path, fileBody, fileOptions) {
  if (this.bucketId !== ATTACHMENTS_BUCKET) {
    return originalUpload.call(this, path, fileBody, fileOptions);
  }
  const safePath = sanitizeAttachmentStoragePath(path);
  // The login token is set on the utils/axios instance, not on this global axios.
  const accessToken = typeof window !== 'undefined' ? window.localStorage.getItem('accessToken') : null;

  try {
    const { data } = await axios.post(`${REST_API_END_POINT}attachments/upload`, fileBody, {
      params: {
        path: safePath,
        upsert: fileOptions?.upsert === true ? 1 : 0,
        type: fileOptions?.contentType || fileBody?.type || 'application/octet-stream',
      },
      headers: {
        'Content-Type': 'application/octet-stream',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      onUploadProgress: fileOptions?.onUploadProgress,
    });
    if (data?.status !== 1 || !data?.path) {
      return { data: null, error: new Error(data?.error || 'Upload failed') };
    }
    return {
      data: { id: data.id, path: data.path, fullPath: `${ATTACHMENTS_BUCKET}/${data.path}` },
      error: null,
    };
  } catch (err) {
    return {
      data: null,
      error: new Error(err?.response?.data?.error || err?.message || 'Upload failed'),
    };
  }
};

storageFileApiProto.getPublicUrl = function patchedGetPublicUrl(path, options) {
  if (this.bucketId !== ATTACHMENTS_BUCKET) {
    return originalGetPublicUrl.call(this, path, options);
  }
  const safePath = sanitizeAttachmentStoragePath(path);
  const result = originalGetPublicUrl.call(this, safePath, options);
  if (result?.data) {
    result.data.publicUrl = buildProxyAttachmentUrl(safePath);
  }
  return result;
};

/** Convert a stored public/authenticated Supabase URL, or another site's proxy URL, to this backend's proxy URL. */
export function toSecureAttachmentUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
  // Keep "#fragment" (e.g. PDF viewer "#toolbar=0") out of the encoded storage path.
  const hashIdx = rawUrl.indexOf('#');
  if (hashIdx !== -1) {
    return toSecureAttachmentUrl(rawUrl.slice(0, hashIdx)) + rawUrl.slice(hashIdx);
  }
  const url = rawUrl;

  const proxyIdx = url.indexOf(PROXY_MARKER);
  if (proxyIdx !== -1) {
    if (!url.startsWith(REST_API_END_POINT)) {
      const path = url.slice(proxyIdx + PROXY_MARKER.length).split('&')[0];
      return ensureHttpsProxyUrl(`${REST_API_END_POINT}attachments/file?path=${path}`);
    }
    return ensureHttpsProxyUrl(url);
  }

  let path = null;
  if (url.includes(PUBLIC_MARKER)) {
    path = url.slice(url.indexOf(PUBLIC_MARKER) + PUBLIC_MARKER.length);
  } else if (url.includes(AUTH_MARKER)) {
    path = url.slice(url.indexOf(AUTH_MARKER) + AUTH_MARKER.length);
  }
  if (!path) return url;
  path = path.split('?')[0];
  try {
    path = decodeURIComponent(path);
  } catch (_) {
    /* keep raw */
  }
  return buildProxyAttachmentUrl(path);
}

function needsSecureRewrite(value) {
  return (
    typeof value === 'string' &&
    (value.includes(PUBLIC_MARKER) ||
      value.includes(AUTH_MARKER) ||
      (value.includes(PROXY_MARKER) && !value.startsWith(REST_API_END_POINT)))
  );
}

// Rewrite remaining direct Supabase URLs used in <img>/<a>/window.open/fetch (hardcoded avatars,
// company logo cached in localStorage, socket chat payloads, etc.).
function patchDomUrlProperty(Constructor, propName) {
  if (typeof window === 'undefined' || !Constructor?.prototype) return;
  const descriptor = Object.getOwnPropertyDescriptor(Constructor.prototype, propName);
  if (!descriptor?.set || !descriptor?.get) return;

  Object.defineProperty(Constructor.prototype, propName, {
    configurable: true,
    enumerable: descriptor.enumerable,
    get() {
      return descriptor.get.call(this);
    },
    set(value) {
      descriptor.set.call(this, needsSecureRewrite(value) ? toSecureAttachmentUrl(value) : value);
    },
  });
}

function installAttachmentUrlMasking() {
  if (typeof window === 'undefined' || window.__signwiseAttachmentUrlMaskInstalled) return;
  window.__signwiseAttachmentUrlMaskInstalled = true;

  patchDomUrlProperty(window.HTMLImageElement, 'src');
  patchDomUrlProperty(window.HTMLSourceElement, 'src');
  patchDomUrlProperty(window.HTMLVideoElement, 'src');
  patchDomUrlProperty(window.HTMLAudioElement, 'src');
  patchDomUrlProperty(window.HTMLIFrameElement, 'src');
  patchDomUrlProperty(window.HTMLAnchorElement, 'href');

  const originalSetAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function setAttributeMasked(name, value) {
    if ((name === 'src' || name === 'href') && needsSecureRewrite(value)) {
      value = toSecureAttachmentUrl(value);
    }
    return originalSetAttribute.call(this, name, value);
  };

  const originalOpen = window.open;
  window.open = function openMasked(url, ...rest) {
    return originalOpen.call(this, needsSecureRewrite(url) ? toSecureAttachmentUrl(url) : url, ...rest);
  };

  if (typeof window.fetch === 'function') {
    const originalFetch = window.fetch;
    window.fetch = function fetchMasked(input, ...rest) {
      return originalFetch.call(this, needsSecureRewrite(input) ? toSecureAttachmentUrl(input) : input, ...rest);
    };
  }
}

installAttachmentUrlMasking();
